import React, { useRef, useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { adminFetch } from '@/utils/adminAuth';
import { ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Image that becomes click-to-replace in edit mode.
 * - path: dotted config path (e.g. "pillars.0.image" or "clients.3.logo")
 * - fallback: URL used when nothing set in config
 * - render: optional custom renderer. Receives (src, extraProps).
 *
 * Upload uses the existing `/api/admin/hero-slides/upload` endpoint which
 * auto-compresses to WebP.
 */
export const EditableImage = ({
  path,
  fallback = '',
  alt = '',
  className = '',
  imgClassName = '',
  render,
  ...rest
}) => {
  const { enabled, getValue, update } = useEditMode();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const configValue = getValue(path);
  const src = configValue || fallback;

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminFetch('/admin/hero-slides/upload', { method: 'POST', body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      if (!data.url) throw new Error('No URL returned');
      update(path, data.url);
      const saved = data.stored_kb;
      const orig = data.original_kb;
      if (orig && saved && orig > saved) {
        toast.success(`Uploaded (compressed ${orig}KB \u2192 ${saved}KB)`);
      } else {
        toast.success('Uploaded');
      }
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // View mode: just render the image (optionally through a custom renderer)
  if (!enabled) {
    if (render) return render(src, { alt, className: imgClassName, ...rest });
    return <img loading="lazy" decoding="async" src={src} alt={alt} className={imgClassName} {...rest} />;
  }

  return (
    <div className={`relative group ${className}`}>
      {render ? render(src, { alt, className: imgClassName, ...rest }) : (
        <img loading="lazy" decoding="async" src={src} alt={alt} className={imgClassName} {...rest} />
      )}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white font-bold text-sm gap-2"
        data-testid={`replace-img-${path}`}
        data-edit-safe
      >
        {uploading ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Uploading...</>
        ) : (
          <><ImagePlus className="w-5 h-5" /> Replace image</>
        )}
      </button>
      <div className="absolute top-2 right-2 pointer-events-none">
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-yellow-400 text-black">
          Click to replace
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onFileChange}
        className="hidden"
      />
    </div>
  );
};

export default EditableImage;
